import SiteConfig from '@/services/SiteConfig'

// Fields copied 1:1 from a /v2 products-search doc onto MetaProduct.
const PASSTHROUGH_FIELDS = [
  'product_id',
  'campaign_id',
  'final_price',
  'score_info',
  'manufacturer_url',
  'milesandmore',
  'delivery',
  'is_enabled',
  'awards',
  'legal_information_string',
  // nutritional_note drives the organic/vegan badges; requires the
  // platform-v2 indexer to expose `details` in the search doc.
  'details',
  'name',
  'type',
  'catch_phrase',
  'reason_why_one',
  'attribute_set',
  'contains_sulfide',
  'campaign_banner',
  'first_enabled_at',
  'alcohol_content',
  'unit_type'
]

/**
 * Resolve a PDP path from a search doc, most-specific source first:
 *   1. url_rewrites[0].request_uri — global products-search docs.
 *   2. full_url — the collection-page products-search endpoint returns an empty
 *      url_rewrites and exposes the full PDP path (slug + `-pdp-p…-c…`) here
 *      instead. (The bare `url` field is only the slug without the -pdp suffix,
 *      so it is NOT a valid PDP link.)
 *   3. id-based fallback — the PDP route (`/:prefix*-pdp-p:pId-c:cId`) allows an
 *      empty prefix, so a bare `-pdp-p<id>-c<id>` always resolves. This keeps
 *      tiles clickable even if both slug fields are missing.
 */
const _resolvePdpPath = (doc) => {
  const requestUri = doc.url_rewrites?.[0]?.request_uri
  if (requestUri) return requestUri
  if (doc.full_url) return doc.full_url
  if (doc.product_id != null && doc.campaign_id != null) {
    return `-pdp-p${doc.product_id}-c${doc.campaign_id}`
  }
  return null
}

// The merchant-configured default quantity. The v1 (solr) endpoint delivers
// it as a string ("6"), the v2 endpoint as a number; coerce and drop blank or
// non-positive values so the CTA falls back to the smallest increment.
const _normalizePreSelectedQty = (value) => {
  const qty = Number(value)
  return qty > 0 ? qty : null
}

/**
 * v2 search delivers tags as a comma-separated string; Tags.vue (and the rest
 * of the FE) expect the array shape meta/bulk products carry. Iterating a raw
 * string in Tags.vue's v-for renders one box per character, and String(null)
 * would surface a literal "null" tag — so null/empty collapse to an empty array.
 */
const _normalizeTags = (tags) => {
  if (Array.isArray(tags)) return tags
  if (tags === null || tags === '') return []
  return String(tags)
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

/**
 * Field copies from a /v2 products-search doc onto MetaProduct, each optionally
 * renamed (`to`) and/or transformed. Undefined source values are skipped so
 * MetaProductSchema defaults are never overridden. Extends PASSTHROUGH_FIELDS
 * with the fields the FE consumes under a different name or shape.
 */
const FIELD_MAPPERS = [
  ...PASSTHROUGH_FIELDS.map((field) => ({ from: field, to: field })),
  { from: 'manufacturer_name', to: 'manufacturer' },
  { from: 'customer_rating', to: 'ratings_average' },
  { from: 'customer_rating_count', to: 'ratings_count' },
  {
    from: 'wine_alcohol_concentration',
    to: 'nutritional_data',
    transform: (value) => ({ wine_alcohol_concentration: value })
  },
  // Tile CTA derives its qty options from qty.values (meta/bulk shape); unit
  // labels fall back to unit_type in SelectOptionFactory.
  {
    from: 'qty_increments',
    to: 'qty',
    transform: (value) => ({ values: value })
  },
  {
    from: 'pre_selected_qty',
    to: 'pre_selected_qty',
    transform: _normalizePreSelectedQty
  },
  { from: 'tags', to: 'tags', transform: _normalizeTags }
]

/**
 * Maps a /v2 products-search doc to MetaProduct field names. Only defined
 * values are returned so MetaProductSchema defaults are never overridden
 * with undefined. Legacy (solr) docs pass through with whatever subset of
 * these fields they carry — their bulk-fetch enrichment stays untouched.
 */
export const mapSearchProduct = (doc) => {
  if (!doc) return {}

  // Marks products originating from the search endpoint — only these may
  // skip the meta/bulk fetch (see resolveSearchProduct); other payloads
  // (cart upsell, PDP meta products) keep their bulk enrichment.
  const mapped = { from_product_search: true }
  for (const { from, to, transform } of FIELD_MAPPERS) {
    const value = doc[from]
    if (value === undefined) continue
    mapped[to] = transform ? transform(value) : value
  }

  // Only rich v2 docs carry the structured price object — never map legacy
  // scalar prices.
  if (doc.price?.final_formatted !== undefined) mapped.price = doc.price

  const pdpPath = _resolvePdpPath(doc)
  if (pdpPath) mapped.url = SiteConfig.pageRoot + pdpPath

  return mapped
}

export default { mapSearchProduct }
